import {
  type StatRow,
  formatCssPercent,
  formatDuration,
  formatPercent,
} from '@betreuung/shared';
import { Swatch } from '@/components/shared/Swatch';
import { cn } from '@/lib/utils';
import { personVars } from '@/lib/personColor';
import type { PersonLookup } from '@/lib/persons';
import { de } from '@/i18n/de';

/** Name and colour of a row; `colorIndex` is `null` for unassigned time. */
export function describeStatRow(
  row: StatRow,
  persons: PersonLookup,
): { name: string; colorIndex: number | null } {
  if (row.personId === null) return { name: de.modal.unassigned, colorIndex: null };
  const person = persons.describe(row.personId);
  return { name: person.name, colorIndex: person.colorIndex };
}

/** React key: the unassigned row has no id but is always present exactly once. */
export function statRowKey(row: StatRow): string {
  return row.personId === null ? 'unassigned' : String(row.personId);
}

export interface StatRowItemProps {
  row: StatRow;
  persons: PersonLookup;
  /** The month view puts a progress bar under the name, the year view stays on one line. */
  variant: 'month' | 'year';
}

export function StatRowItem({ row, persons, variant }: StatRowItemProps) {
  const { name, colorIndex } = describeStatRow(row, persons);
  const percent = formatPercent(row.fraction);
  const duration = formatDuration(row.slots);

  if (variant === 'year') {
    return (
      <div className="flex items-center gap-[9px] border-b border-divider py-2">
        <Swatch colorIndex={colorIndex} size={12} radius={4} />
        <span className="min-w-0 flex-1 truncate text-body font-medium text-fg">{name}</span>
        <span className="shrink-0 text-body font-bold text-fg">{percent}</span>
        <span className="w-[86px] shrink-0 text-right font-mono text-duration font-medium text-fg-muted">
          {duration}
        </span>
      </div>
    );
  }

  return (
    <div className="border-b border-divider py-2.5">
      <div className="flex items-center gap-[9px]">
        <Swatch colorIndex={colorIndex} size={12} radius={4} />
        <span className="min-w-0 flex-1 truncate text-row font-medium text-fg">{name}</span>
        <span className="shrink-0 text-row font-bold text-fg">{percent}</span>
      </div>
      <div className="flex items-center gap-[9px] pt-1.5 pl-[21px]">
        <span className="h-1 flex-1 overflow-hidden rounded-sm bg-divider">
          <span
            style={{
              width: formatCssPercent(row.fraction),
              ...(colorIndex === null ? {} : personVars(colorIndex)),
            }}
            className={cn(
              'block h-full rounded-sm',
              colorIndex === null ? 'bg-free-bar' : 'person-bg',
            )}
          />
        </span>
        <span className="shrink-0 font-mono text-hint font-medium text-fg-muted">{duration}</span>
      </div>
    </div>
  );
}

/** Row of the year view's "Durchschnitt pro Monat" section. */
export function StatAverageRow({ row, persons }: { row: StatRow; persons: PersonLookup }) {
  const { name, colorIndex } = describeStatRow(row, persons);

  return (
    <div className="flex items-center gap-[9px] border-b border-divider py-[7px]">
      <Swatch colorIndex={colorIndex} size={12} radius={4} />
      <span className="min-w-0 flex-1 truncate text-body font-medium text-fg">{name}</span>
      <span className="shrink-0 font-mono text-hint font-medium text-fg-muted">
        {de.stats.average(formatDuration(Math.round(row.slots / 12)))}
      </span>
    </div>
  );
}
